import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';

export interface SlideContent {
  id: string | number;
  title: string;
  description: string;
  thumbnails: string[];
}

interface SingleSlideProps {
  slide: SlideContent;
  activeImageIndex: number;
  onImageChange: (index: number) => void;
}

const DesktopSlide: React.FC<SingleSlideProps> = ({
  slide,
  activeImageIndex,
  onImageChange,
}) => {
  return (
    <div className="program-slider-content">
      <div className="program-slider-img-container">
        <img
          src={slide.thumbnails[activeImageIndex]}
          alt={slide.title}
          className="program-slider-img program-slider-img-main"
        />
      </div>

      <div className="program-slider-descriptive-content">
        <div className="program-slider-text">
          <h2 className="program-slider-title">{slide.title}</h2>
          <p className="program-slider-description">{slide.description}</p>
        </div>

        <div className="program-slider-img-smaller">
          {slide.thumbnails.map((thumb, index) => (
            <div
              key={index}
              className={`program-slider-small-container ${
                index === activeImageIndex ? 'active' : ''
              }`}
              onClick={() => onImageChange(index)}
            >
              <img src={thumb} alt="" className="program-slider-img" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MobileSlide: React.FC<SingleSlideProps> = ({
  slide,
  activeImageIndex,
  onImageChange,
}) => {
  return (
    <div className="program-slider-content-mobile">
      <div className="program-slider-text">
        <h2 className="program-slider-title">{slide.title}</h2>
        <p className="program-slider-description">{slide.description}</p>
      </div>

      <div className="program-slider-visuals-mobile">
        <div className="program-slider-img-container">
          <img
            src={slide.thumbnails[activeImageIndex]}
            alt={slide.title}
            className="program-slider-img program-slider-img-main"
          />
        </div>

        <div className="program-slider-thumbnails-mobile">
          {slide.thumbnails.map((thumb, index) => (
            <div
              key={index}
              className={`program-slider-small-container ${
                index === activeImageIndex ? 'active' : ''
              }`}
              onClick={() => onImageChange(index)}
            >
              <img src={thumb} alt="" className="program-slider-img" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface ProgramSliderProps {
  slides: SlideContent[];
}

export const ProgramSlider: React.FC<ProgramSliderProps> = ({ slides }) => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  const [selections, setSelections] = useState<Record<string | number, number>>(
    {},
  );

  const handleImageChange = (slideId: string | number, index: number) => {
    setSelections(prev => ({
      ...prev,
      [slideId]: index,
    }));
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section
      className="program-slider-section"
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      <svg
        className="program-slider-glow-svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1848 2130"
        fill="none"
      >
        <rect
          x="-281.737"
          width="2129.74"
          height="2129.73"
          rx="1064.87"
          fill="url(#paint0_radial_slider_glow)"
        />
        <defs>
          <radialGradient
            id="paint0_radial_slider_glow"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(783.132 1064.87) rotate(90) scale(1064.87 1064.87)"
          >
            <stop stopColor="#FF2238" stopOpacity="0.16" />
            <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>

      <div className="program-slider-carousel-wrapper">
        {isMobile ? (
          <div
            className="program-slider-mobile-stack"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '100px',
            }}
          >
            {slides.map(slide => {
              const currentIndex = selections[slide.id] || 0;

              return (
                <div key={slide.id}>
                  <MobileSlide
                    slide={slide}
                    activeImageIndex={currentIndex}
                    onImageChange={newIndex =>
                      handleImageChange(slide.id, newIndex)
                    }
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <Slider {...settings}>
            {slides.map(slide => {
              const currentIndex = selections[slide.id] || 0;

              return (
                <div key={slide.id} className="program-slider-slide">
                  {/* Desktop Wrapper */}
                  <div className="desktop-slide-wrapper">
                    <DesktopSlide
                      slide={slide}
                      activeImageIndex={currentIndex}
                      onImageChange={newIndex =>
                        handleImageChange(slide.id, newIndex)
                      }
                    />
                  </div>

                  <div className="mobile-slide-wrapper">
                    <MobileSlide
                      slide={slide}
                      activeImageIndex={currentIndex}
                      onImageChange={newIndex =>
                        handleImageChange(slide.id, newIndex)
                      }
                    />
                  </div>
                </div>
              );
            })}
          </Slider>
        )}
      </div>
    </section>
  );
};
